import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

const LandingPage = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleGetStarted = () => navigate('/login');
  const handleLogin = () => navigate('/login');

  const services = [
    {
      icon: '◈',
      title: 'Residency List',
      description: 'Curated IMG-friendly programs tailored to your scores, visa status, and graduation year.',
    },
    {
      icon: '◎',
      title: 'Program Analysis',
      description: 'Data-driven insights across 600+ programs to identify your strongest match candidates.',
    },
    {
      icon: '◇',
      title: 'Smart Filtering',
      description: 'Filter by specialty, location, USMLE scores, USCE requirements, and visa sponsorship.',
    },
    {
      icon: '○',
      title: 'Profile Builder',
      description: 'Build a comprehensive applicant profile and get personalized program recommendations.',
    },
  ];

  const testimonials = [
    {
      quote: 'Residency Companion helped me narrow down from 200 programs to a focused list of 80. I matched on my first cycle.',
      name: 'Dr. Arjun Mathur',
      specialty: 'Internal Medicine',
      initials: 'AM',
      image: 'Arjun.jpg',
    },
    {
      quote: 'The IMG-friendly filters saved me thousands in application fees and countless hours of research.',
      name: 'Dr. Priya Khandelwal',
      specialty: 'Family Medicine',
      initials: 'PK',
      image: 'Priya.jpg',
    },
    {
      quote: 'Finally a tool that understands what IMGs actually need. The data is incredibly accurate.',
      name: 'Dr. Alex Sanchez',
      specialty: 'Psychiatry',
      initials: 'SR',
      image: 'alex.jpg',
    },
    {
      quote: 'The compatibility scores and program list made my application strategy clear. Matched at my top choice.',
      name: 'Dr. Emily Dlamini',
      specialty: 'Internal Medicine',
      initials: 'KM',
      image: 'emily.jpg',
    },
  ];

  const reviewStats = [
    { value: '600+', label: 'Programs' },
    { value: '4K+', label: 'IMGs Served' },
    { value: '500+', label: 'Lists Created' },
    { value: '$10', label: 'Starting Price' },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif' }}>

      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-xl border-b border-black/8 shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-2.5">
              <img
                src="./images/logo.png"
                alt="Residency Companion"
                className="w-7 h-7 rounded-lg"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="w-7 h-7 rounded-lg bg-black flex items-center justify-center" style={{ display: 'none' }}>
                <span className="text-white font-semibold text-xs tracking-tight">RC</span>
              </div>
              <span className="text-[15px] font-semibold text-black tracking-tight">Residency Companion</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleLogin}
                className="text-[13px] font-medium text-gray-600 hover:text-black px-4 py-1.5 rounded-full transition-colors duration-200"
              >
                Sign In
              </button>
              <button
                onClick={handleGetStarted}
                className="text-[13px] font-medium bg-black hover:bg-gray-800 text-white px-5 py-1.5 rounded-full transition-all duration-200"
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col items-center justify-center pt-14 overflow-hidden bg-white">
        {/* Subtle grid */}
        <div className="absolute inset-0 opacity-[0.025]" style={{
          backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)',
          backgroundSize: '64px 64px'
        }} />

        {/* Monochrome hand + stethoscope background - behind text */}
        <div
          className="absolute inset-0 z-[1] pointer-events-none"
          style={{
            backgroundImage: 'url(/images/hand1.jpg)',
            backgroundSize: 'contain',
            backgroundPosition: '105% center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.18,
          }}
        />

        <div className={`relative z-10 text-center px-6 max-w-4xl mx-auto transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="mb-8 inline-flex items-center gap-2 border border-black/10 rounded-full px-4 py-1.5 bg-black/[0.02]">
            <span className="w-1.5 h-1.5 rounded-full bg-black inline-block"></span>
            <span className="text-[12px] text-gray-500 font-medium tracking-wide uppercase">IMG-Friendly · Data-Driven · Affordable</span>
          </div>

          <h1 className="text-[64px] md:text-[88px] font-semibold text-black leading-[1.05] tracking-[-0.04em] mb-6">
            Find your
            <br />
            <span className="text-blue-600 font-light">Perfect Residency Match</span>
          </h1>

          <p className="text-[18px] md:text-[20px] text-gray-500 mb-10 max-w-xl mx-auto font-normal leading-relaxed tracking-[-0.01em]">
            Spend $10, save thousands. The smartest way for IMGs to build a targeted residency program list.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <button
              onClick={handleGetStarted}
              className="group bg-black hover:bg-gray-800 text-white px-8 py-3.5 rounded-full text-[15px] font-medium transition-all duration-200 flex items-center gap-2 tracking-[-0.01em]"
            >
              Start for free
              <span className="transition-transform duration-200 group-hover:translate-x-0.5">→</span>
            </button>
            <button
              onClick={handleGetStarted}
              className="border border-black/15 hover:border-black/30 text-black px-8 py-3.5 rounded-full text-[15px] font-medium transition-all duration-200 tracking-[-0.01em] bg-white hover:bg-gray-50"
            >
              View programs
            </button>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 opacity-30">
          <div className="w-[1px] h-8 bg-black animate-scroll-line"></div>
        </div>
      </section>

      {/* Divider */}
      <div className="border-t border-black/6" />

      {/* Stats Strip */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 divide-x divide-black/6">
            {[
              { value: '600+', label: 'Programs' },
              { value: 'IMG', label: 'Focused' },
              { value: '$10', label: 'Starting Price' },
              { value: '24/7', label: 'Support' },
            ].map((stat) => (
              <div key={stat.label} className="text-center px-8 py-4">
                <div className="text-[28px] md:text-[32px] font-semibold text-black tracking-[-0.03em] mb-1">{stat.value}</div>
                <div className="text-[12px] text-gray-400 font-medium uppercase tracking-widest">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="border-t border-black/6" />

      {/* Features: 4 pillars */}
      <section className="py-28 md:py-36 bg-[#fafafa]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20 md:mb-24">
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-[0.2em] mb-5">Features</p>
            <h2 className="text-[36px] md:text-[48px] font-semibold text-black tracking-[-0.035em] leading-[1.12] max-w-2xl mx-auto mb-5">
              Everything you need to match.
            </h2>
            <p className="text-[15px] md:text-[16px] text-gray-500 max-w-lg mx-auto leading-relaxed tracking-[-0.01em]">
              Four tools that work together to get you from profile to program list.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {services.map((service, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-black/[0.06] p-8 md:p-10 group hover:border-black/[0.1] hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-300 ease-out"
              >
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-black/[0.05] flex items-center justify-center text-black/60 group-hover:bg-black/[0.08] group-hover:text-black transition-all duration-300 text-[20px] font-light select-none">
                    {service.icon}
                  </div>
                  <div className="min-w-0 pt-0.5">
                    <h3 className="text-[17px] md:text-[18px] font-semibold text-black mb-2 tracking-[-0.02em]">
                      {service.title}
                    </h3>
                    <p className="text-[14px] md:text-[15px] text-gray-500 leading-[1.6] tracking-[-0.01em]">
                      {service.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="border-t border-black/6" />

      {/* How It Works */}
      <section className="py-24 bg-[#fafafa]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="mb-16 text-center">
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-widest mb-4">Process</p>
            <h2 className="text-[40px] md:text-[52px] font-semibold text-black tracking-[-0.03em] leading-[1.1]">
              Three steps to your list.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              {
                step: '01',
                title: 'Enter your profile',
                desc: 'Input your USMLE scores, visa status, graduation year, and USCE to build your applicant profile.',
              },
              {
                step: '02',
                title: 'Analyze programs',
                desc: 'Our system cross-references your profile against historical data from 600+ IMG-friendly programs.',
              },
              {
                step: '03',
                title: 'Get your list',
                desc: 'Receive a curated, ranked list of programs where applicants like you have successfully matched.',
              },
            ].map((item, i) => (
              <div key={i} className="group">
                <div className="text-[11px] font-medium text-gray-300 mb-6 tracking-widest font-mono">{item.step}</div>
                <div className="w-full h-px bg-black/10 mb-6 group-hover:bg-black/40 transition-colors duration-300" />
                <h3 className="text-[20px] font-semibold text-black mb-3 tracking-[-0.02em]">{item.title}</h3>
                <p className="text-[14px] text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="border-t border-black/6" />

      {/* Testimonials & Stats */}
      <section className="py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="mb-16">
            <p className="text-[11px] font-medium text-gray-400 uppercase tracking-widest mb-4">Reviews</p>
            <h2 className="text-[40px] md:text-[52px] font-semibold text-black tracking-[-0.03em] leading-[1.1] max-w-lg">
              From IMGs who matched.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="group rounded-2xl border border-black/[0.08] overflow-hidden bg-white hover:border-black/15 hover:shadow-md transition-all duration-300"
              >
                <div className="aspect-[4/3] bg-gray-100 flex items-center justify-center overflow-hidden">
                  <img
                    src={`/images/${t.image}`}
                    alt={t.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="relative -mt-12 mx-4 mb-4 rounded-xl bg-white/95 backdrop-blur-sm border border-black/[0.06] shadow-sm p-4">
                  <p className="text-[13px] text-gray-700 leading-relaxed mb-4 line-clamp-3">"{t.quote}"</p>
                  <div className="flex items-center gap-3">
                    <img
                      src={`/images/${t.image}`}
                      alt={t.name}
                      className="w-9 h-9 rounded-full object-cover flex-shrink-0"
                    />
                    <div>
                      <div className="text-[13px] font-semibold text-black">{t.name}</div>
                      <div className="text-[11px] text-gray-500">Matched {t.specialty}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {reviewStats.map((stat, i) => (
              <div
                key={stat.label}
                className={`rounded-2xl border p-6 flex flex-col justify-center transition-all duration-300 ${
                  i === 1
                    ? 'bg-black border-black text-white hover:bg-gray-900'
                    : 'bg-white border-black/[0.08] hover:border-black/15 hover:shadow-md'
                }`}
              >
                <div className={`text-[12px] font-medium uppercase tracking-widest mb-2 ${i === 1 ? 'text-white/70' : 'text-gray-400'}`}>
                  {stat.label}
                </div>
                <div className={`text-[32px] md:text-[36px] font-semibold tracking-[-0.03em] ${i === 1 ? 'text-white' : 'text-black'}`}>
                  {stat.value}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="border-t border-black/6" />

      {/* CTA Section */}
      <section className="py-24 bg-black">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-[11px] font-medium text-white/30 uppercase tracking-widest mb-6">Get started today</p>
          <h2 className="text-[40px] md:text-[64px] font-semibold text-white tracking-[-0.04em] leading-[1.05] mb-6">
            Apply smarter.
            <br />
            <span className="text-white/40 font-light">Match faster.</span>
          </h2>
          <p className="text-[16px] text-white/50 mb-10 max-w-md mx-auto leading-relaxed">
            Join thousands of IMGs who used data — not guesswork — to build their program lists.
          </p>
          <button
            onClick={handleGetStarted}
            className="group bg-white hover:bg-gray-100 text-black px-10 py-4 rounded-full text-[15px] font-semibold transition-all duration-200 flex items-center gap-2 mx-auto tracking-[-0.01em]"
          >
            Start for $10
            <span className="transition-transform duration-200 group-hover:translate-x-0.5">→</span>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-black/6">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img
                  src="./images/logo.png"
                  alt="Residency Companion"
                  className="w-6 h-6 rounded-md"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-6 h-6 rounded-md bg-black flex items-center justify-center" style={{ display: 'none' }}>
                  <span className="text-white font-bold text-[9px]">RC</span>
                </div>
                <span className="text-[14px] font-semibold text-black tracking-tight">Residency Companion</span>
              </div>
              <p className="text-[13px] text-gray-400 leading-relaxed">
                Your partner in medical education decisions.
              </p>
            </div>

            <div>
              <p className="text-[11px] font-semibold text-black uppercase tracking-widest mb-4">Product</p>
              <ul className="space-y-2.5">
                {[['Features', '/features'], ['How It Works', '/how-it-works']].map(([label, href]) => (
                  <li key={label}>
                    <Link to={href} className="text-[13px] text-gray-400 hover:text-black transition-colors duration-150">{label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="text-[11px] font-semibold text-black uppercase tracking-widest mb-4">Support</p>
              <ul className="space-y-2.5">
                {[
                  ['Help Center', '/help'],
                  ['Contact Us', '/contact'],
                  ['Refund Policy', '/refund-policy'],
                  ['Cancellation Policy', '/cancellation-policy'],
                  ['Privacy', '/privacy'],
                  ['Terms', '/terms'],
                ].map(([label, href]) => (
                  <li key={label}>
                    <Link to={href} className="text-[13px] text-gray-400 hover:text-black transition-colors duration-150">{label}</Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <p className="text-[11px] font-semibold text-black uppercase tracking-widest mb-4">Company</p>
              <ul className="space-y-2.5">
                <li>
                  <Link to="/about" className="text-[13px] text-gray-400 hover:text-black transition-colors duration-150">About</Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-black/6 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-[12px] text-gray-400">
              © 2025 Residency Companion. All rights reserved.
            </p>
            <p className="text-[12px] text-gray-300">
              ERAS® and NRMP® are registered trademarks of their respective owners.
            </p>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes scroll-line {
          0% { transform: scaleY(0); transform-origin: top; }
          50% { transform: scaleY(1); transform-origin: top; }
          51% { transform: scaleY(1); transform-origin: bottom; }
          100% { transform: scaleY(0); transform-origin: bottom; }
        }
        .animate-scroll-line {
          animation: scroll-line 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
